import { useRef } from 'react';
import { useInView, motion } from 'motion/react';
import { Box, Stack } from '@mui/material';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import SectionLayout from './SectionLayout';

function BioSection({ bioRef, onScrollUp, onScrollDown, style }) {
  const inViewRef = useRef(null);
  const inView = useInView(inViewRef, { amount: 0.3 });

  const setRefs = (el) => {
    bioRef.current = el;
    inViewRef.current = el;
  };

  return (
    <SectionLayout
      sectionRef={setRefs}
      style={style}
      onScrollUp={onScrollUp}
      onScrollDown={onScrollDown}
      arrowsVisible={inView}
    >
      <Stack spacing={4} sx={{ maxWidth: '450px', padding: '2rem', textAlign: 'center' }}>
        <h2 style={{ fontSize: '36px', textShadow: '0 0 18px rgba(255,255,255,0.45)' }}>About Me</h2>
        <p style={{ fontSize: '18px', lineHeight: '1.8' }}>
          I enjoy creating software that's genuinely fun to use, not just functional, and bringing a bit
          of creativity to the process. When I'm away from the keyboard, I'm usually off making things in the real world.
        </p>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ color: 'white', width: '100%' }}>
          <Box sx={{ position: 'relative', flex: 1, height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.5))' }}>
            <motion.span
              style={{
                position: 'absolute',
                top: '50%',
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: '0 0 6px 1px rgba(255,255,255,0.85)',
                transform: 'translate(-50%, -50%)',
              }}
              animate={{ left: ['0%', '92%', '100%'], opacity: [0, 1, 0] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: 'linear', repeatDelay: 0.8, times: [0, 0.85, 1] }}
            />
          </Box>
          <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Flash of light behind the chip, timed to when the pulses arrive. */}
            <motion.span
              aria-hidden
              style={{
                position: 'absolute',
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.85), rgba(255,255,255,0) 70%)',
                pointerEvents: 'none',
              }}
              animate={{ opacity: [0, 0, 0.9, 0], scale: [0.5, 0.5, 1.25, 0.85] }}
              transition={{ duration: 3.4, repeat: Infinity, ease: 'easeOut', times: [0, 0.66, 0.75, 0.92] }}
            />
            <DeveloperBoardIcon sx={{ fontSize: 26, opacity: 0.7, position: 'relative' }} />
          </Box>
          <Box sx={{ position: 'relative', flex: 1, height: '1px', background: 'linear-gradient(to left, transparent, rgba(255,255,255,0.5))' }}>
            <motion.span
              style={{
                position: 'absolute',
                top: '50%',
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: '0 0 6px 1px rgba(255,255,255,0.85)',
                transform: 'translate(-50%, -50%)',
              }}
              animate={{ left: ['100%', '8%', '0%'], opacity: [0, 1, 0] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: 'linear', repeatDelay: 0.8, times: [0, 0.85, 1] }}
            />
          </Box>
        </Stack>
        <p style={{ fontSize: '18px', lineHeight: '1.8' }}>
          My favorite projects combine hardware and software into physical gadgets and games, using 3D
          printing to bring them to life.
        </p>
      </Stack>
    </SectionLayout>
  );
}

export default BioSection;
